import { useState } from 'react';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

interface ResolvedPayload {
  kind: string;
  locker_room?: { public_name?: string };
  public_name?: string;
  [key: string]: unknown;
}

interface DonateShareCodeInputProps {
  onResolved?: (payload: ResolvedPayload, kind: string) => void;
}

export default function DonateShareCodeInput({ onResolved }: DonateShareCodeInputProps) {
  const [code, setCode] = useState('');
  const [res, setRes] = useState<ResolvedPayload | null>(null);
  const [err, setErr] = useState('');

  const resolve = async () => {
    setErr('');
    setRes(null);
    const trimmed = code.trim();
    if (!trimmed) {
      setErr('Enter a share code to continue');
      return;
    }

    const tryResolve = async (url: string, kind: string) => {
      const response = await fetch(url);
      if (!response.ok) return null;
      const raw = await response.json();
      const payload = raw?.data || raw;
      return { kind, payload };
    };

    const classroomResult = await tryResolve(
      `${CORE_API}/api/v1/community/share-resolve?code=${encodeURIComponent(trimmed)}`,
      'classroom',
    );

    const lockerResult =
      classroomResult ||
      (await tryResolve(
        `${CORE_API}/api/v1/locker-room/donate-link?code=${encodeURIComponent(trimmed)}`,
        'locker_room',
      ));

    if (!lockerResult) {
      setErr('Not found');
      return;
    }

    const resultPayload = { ...lockerResult.payload, kind: lockerResult.kind };
    setRes(resultPayload);
    onResolved?.(resultPayload, lockerResult.kind);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">Have a share code?</label>
      <div className="flex gap-2">
        <input
          value={code}
          onChange={(e)=>setCode(e.target.value.toUpperCase())}
          className="flex-1 rounded-lg border px-3 py-2"
          placeholder="PT-ABC123"
        />
        <button type="button" onClick={resolve} className="rounded-lg bg-brick text-white px-3 py-2">
          Resolve
        </button>
      </div>
      {err && <p className="text-sm text-brick">{err}</p>}
      {res && (
        <div className="text-sm text-cocoa/80">
          Ready to support this {res.kind === 'locker_room' ? 'locker room' : 'classroom'}:
          {' '}
          <span className="font-semibold">{res.locker_room?.public_name || res.public_name || 'Classroom'}</span>
        </div>
      )}
    </div>
  );
}
